import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { CenteredSpinner } from "@/components/shared/Loader";
import VariableChips from "@/pages/admin/outreach-settings/components/VariableChips";
import { Check, Copy, Link2, X } from "lucide-react";
import toast from "react-hot-toast";

import { ComplianceService } from "@/services/complianceService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

/**
 * Step 2 of Send to Compliance — the email the candidate actually receives.
 *
 * Step 1 (`SendToComplianceModal`) picks the checklist and hands it here. This
 * modal loads the tenant's compliance template filled in for this candidate,
 * lets the recruiter reword it, and sends. It is a SIBLING of step 1, hosted by
 * the page with close-then-reopen — never nested inside step 1's DialogContent,
 * which would create a stacking context it can never paint above (React error
 * #299), the same trap `SendToComplianceModal`'s `onViewChecklist` comment
 * records.
 *
 * ## The link is the message
 *
 * The compliance portal link is a bearer credential and it is the entire point
 * of the mail: a compliance email without it tells the candidate they have been
 * asked for documents and gives them no way to supply them. So Send is dead
 * until the body carries a link, and the server re-checks the same thing on the
 * rendered output (`COMPLIANCE_PORTAL_LINK_REQUIRED`). Two checks on purpose —
 * this one so the recruiter hears about it before they lose the edit, the
 * server's so calling the API directly gains nothing.
 *
 * ⚠️ The check accepts only `{{compliance_portal_link}}`. The send mints the
 * portal token, so no URL the recruiter could paste is this run's link: it is
 * another candidate's bearer link, or none.
 *
 * ## Preview
 *
 * `compliance_portal_link` and `portal_expiry_date` describe a token that does
 * not exist yet — no run has been created, and the preview endpoint deliberately
 * creates none (minting a token for a recruiter who then hits Cancel would leave
 * a live credential behind for a run that never existed). Both arrive as literal
 * `{{tokens}}`, and Preview draws them as shape-accurate placeholders so the
 * recruiter can see where the link lands.
 */

const PORTAL_LINK_TOKEN = "{{compliance_portal_link}}";

/**
 * What Preview shows for the two variables the send has not resolved yet.
 * Shape-accurate rather than blank: a preview that renders the link as an empty
 * string reads as fine, and the recruiter approves an email with a hole in it.
 */
const PREVIEW_PLACEHOLDERS = {
  compliance_portal_link: "https://…/compliance-portal/…",
  portal_expiry_date: "(90 days from sending)",
};

/**
 * Does this body carry a way to reach the portal? Only the variable does — see
 * the class comment. Mirrors the server's gate, which runs on the RENDERED body.
 */
export const bodyHasPortalLink = (body) => (body || "").includes(PORTAL_LINK_TOKEN);

/** Substitutes the placeholders Preview knows about; leaves the rest alone. */
const renderPreview = (text) => (text || "").replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (match, name) => PREVIEW_PLACEHOLDERS[name] ?? match);

const ComplianceEmailModal = ({ open, onClose, onBack, candidate, applicationId, checklistId, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateFound, setTemplateFound] = useState(true);
  const [candidateEmail, setCandidateEmail] = useState("");
  // The variables the compliance send resolves — served by the preview, so the
  // chips and the server's renderer read one list.
  const [variables, setVariables] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // A refusal the recruiter has to read and act on — the wording is theirs to
  // fix — rather than a toast that vanishes while they are still looking at it.
  const [refusal, setRefusal] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const bodyRef = useRef(null);
  const portalInputRef = useRef(null);
  const caretRef = useRef(null);
  const pendingCaretRef = useRef(null);

  // Every piece of state above resets here, on open.
  //
  // This modal is never unmounted — the page keeps it mounted and flips `open`.
  // A subject and body left over from the last candidate would be sent to the
  // next one: a wrong-recipient email, not a cosmetic bug. Anything added to
  // this component joins this block.
  useEffect(() => {
    if (!open) return undefined;

    setSubject("");
    setBody("");
    setTemplateFound(true);
    setCandidateEmail("");
    setVariables([]);
    setPreviewing(false);
    setSubmitting(false);
    setRefusal("");
    setPortalUrl("");
    setCopied(false);
    setLoadError("");
    caretRef.current = null;

    if (!candidate?.id || !checklistId) return undefined;

    let active = true;
    setLoading(true);
    ComplianceService.getSendPreview({ candidateId: candidate.id, checklistId, applicationId })
      .then((res) => {
        if (!active) return;
        setSubject(res?.subject || "");
        setBody(res?.body || "");
        setTemplateFound(res?.templateFound !== false);
        setCandidateEmail(res?.candidateEmail || "");
        setVariables(res?.variables || []);
      })
      .catch((e) => {
        console.error(e);
        if (!active) return;
        // An empty editor with no explanation looks like a template with no
        // words in it. Say which of the three screens this is.
        setLoadError(apiErrorMessageFormat(e, "Could not load the compliance email."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, candidate?.id, checklistId, applicationId]);

  // Insert a variable at the recruiter's caret rather than at the end of the
  // text, following MessageTemplatesPanel's insert. The caret restore runs in
  // useLayoutEffect for the reason recorded there: assigning a textarea's value
  // resets its selection, so a rAF callback could wipe the caret we had just
  // positioned.
  const handleInsertVar = (snippet) => {
    const el = bodyRef.current;
    const current = body || "";
    const pos = caretRef.current;
    const start = typeof pos?.start === "number" ? pos.start : current.length;
    const end = typeof pos?.end === "number" ? pos.end : current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    const caret = start + snippet.length;

    caretRef.current = { start: caret, end: caret };
    pendingCaretRef.current = { el, caret };
    setBody(next);
  };

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    if (!pending) return;
    pendingCaretRef.current = null;
    const { el, caret } = pending;
    if (!el) return;
    el.focus();
    el.setSelectionRange(caret, caret);
  });

  const hasLink = bodyHasPortalLink(body);
  const canSend = !!body.trim() && hasLink && !submitting && !loading && !!candidate?.id;

  const handleCopy = async () => {
    // navigator.clipboard only exists in a secure context (HTTPS or localhost).
    // On a plain-HTTP LAN host it is undefined and the async API throws, so fall
    // back to select + execCommand("copy"), which works there too.
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(portalUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const el = portalInputRef.current;
        if (el) {
          el.focus();
          el.select();
          el.setSelectionRange(0, portalUrl.length);
          ok = document.execCommand("copy");
          el.setSelectionRange(0, 0);
          el.blur();
        }
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Portal link copied");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Could not copy — please select the link and copy it manually.");
    }
  };

  const handleSend = async () => {
    if (!canSend) return;

    setSubmitting(true);
    setRefusal("");
    try {
      const res = await ComplianceService.sendFromRecruitment({
        candidateId: candidate.id,
        checklistId,
        applicationId,
        subject,
        body,
      });

      onSent?.(candidate.id);

      // The email carried the link, so there is nothing left to hand over.
      // Only when it did not go out (no address, or the send failed) does the
      // recruiter become the delivery path, and only then is the link shown.
      if (res?.emailSent === false && res?.portalUrl) {
        toast.error("Sent to compliance, but the email did not go out. Share the link below with the candidate.");
        setPortalUrl(res.portalUrl);
        return;
      }
      toast.success("Compliance email sent");
      onClose?.();
    } catch (e) {
      console.error(e);

      // Branch on `error_code`, never on the status alone — the same rule step
      // 1's three-way 409 follows.
      const code = e?.response?.data?.error_code;
      if (code === "COMPLIANCE_PORTAL_LINK_REQUIRED") {
        // The recruiter's own wording is what has to change, so the message
        // stays on screen beside the editor they will change it in.
        setRefusal(apiErrorMessageFormat(e, "This email must contain the candidate's portal link."));
        return;
      }
      if (code === "COMPLIANCE_CANDIDATE_EXISTS") {
        toast(apiErrorMessageFormat(e, "Candidate is already in compliance"));
        onSent?.(candidate.id);
        onClose?.();
        return;
      }
      if (e?.response?.status === 409) {
        // A real refusal — another job owns the run, or the candidate never
        // reached the trigger stage. The server's sentence names which, and it
        // is the only explanation the recruiter gets.
        setRefusal(apiErrorMessageFormat(e, "This candidate cannot be sent to compliance right now."));
        return;
      }

      toast.error(apiErrorMessageFormat(e, "Failed to send the compliance email"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.();
      }}
    >
      <DialogContent className="w-[620px] md:w-full flex flex-col p-0">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">{portalUrl ? "Sent to compliance" : "Compliance email"}</DialogTitle>
            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
              {portalUrl
                ? "The email did not go out — share the upload link with the candidate."
                : candidateEmail
                  ? `This goes to ${candidateEmail}.`
                  : "Review what the candidate receives, then send."}
            </DialogDescription>
          </div>

          <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        {portalUrl ? (
          /* Only reached when the email did not go out — see handleSend. */
          <div className="px-5 py-4" style={{ zoom: 0.85 }}>
            <div className="flex items-center gap-2 text-[13px] font-medium text-text-primary mb-1.5">
              <Link2 size={15} />
              Candidate upload portal link
            </div>
            <p className="text-[12px] text-text-primary/70 mb-2">They can upload their documents here directly, no login needed.</p>

            <div className="flex items-center gap-2">
              <Input
                ref={portalInputRef}
                type="text"
                readOnly
                value={portalUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 rounded-xl bg-background-icon/40 border border-border-primary px-3 py-2 text-[12px] text-text-primary outline-none"
              />
              <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button className="bg-button-secondary text-text-base" type="button" onClick={() => onClose?.()}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4" style={{ zoom: 0.85 }}>
            {loading ? (
              <CenteredSpinner className="h-40" />
            ) : loadError ? (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[12px] text-red-700">{loadError}</div>
            ) : (
              <>
                {!templateFound && (
                  <div className="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-700">
                    No compliance email template is set up yet. Write the message below — it must include the candidate&apos;s portal link.
                  </div>
                )}

                <label htmlFor="compliance-email-subject" className="block text-[13px] font-medium text-text-primary mb-1.5">
                  Subject
                </label>
                {previewing ? (
                  <div className="rounded-xl border border-border-primary bg-background-icon/40 px-3 py-2 text-[13px] text-text-primary">{renderPreview(subject)}</div>
                ) : (
                  <Input
                    id="compliance-email-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={submitting}
                    className="rounded-xl bg-background-icon/40 px-3 py-2 text-[13px]"
                  />
                )}

                <div className="mt-3 flex items-center justify-between">
                  <label htmlFor="compliance-email-body" className="block text-[13px] font-medium text-text-primary">
                    Message
                  </label>
                  <Button type="button" variant="outline" size="xs" onClick={() => setPreviewing((p) => !p)}>
                    {previewing ? "Edit" : "Preview"}
                  </Button>
                </div>

                {previewing ? (
                  /* Read-only on purpose: Preview is what the candidate sees,
                    and an editable preview is two editors that can disagree. */
                  <div className="mt-1.5 min-h-[180px] whitespace-pre-wrap rounded-xl border border-border-primary bg-background-icon/40 px-3 py-2 text-[13px] text-text-primary">
                    {renderPreview(body)}
                  </div>
                ) : (
                  <>
                    <Textarea
                      id="compliance-email-body"
                      ref={bodyRef}
                      rows={10}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      onSelect={(e) => {
                        caretRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      onKeyUp={(e) => {
                        caretRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      disabled={submitting}
                      className="mt-1.5 font-mono text-[12px]"
                    />
                    <VariableChips variables={variables} onInsert={handleInsertVar} className="mt-2" />
                  </>
                )}

                {/* Why Send is dead. Without this the button is disabled and
                  the recruiter has no idea which of the three reasons applies. */}
                {!previewing && !!body.trim() && !hasLink && (
                  <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-700">
                    This email has no upload link, so the candidate would have no way to send their documents. Insert{" "}
                    <code className="font-mono">{PORTAL_LINK_TOKEN}</code>.
                  </div>
                )}

                {refusal && <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-700">{refusal}</div>}
              </>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="tertiary" type="button" disabled={submitting} onClick={() => onBack?.()}>
                Back
              </Button>
              <Button className="bg-button-secondary text-text-base" type="button" onClick={handleSend} disabled={!canSend}>
                {submitting ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComplianceEmailModal;
